/**
 * Shared PDF Text Utilities
 *
 * Consolidates text processing, character encoding, content normalization,
 * text wrapping, and safe rendering logic used across all PDF generators.
 *
 * Implements a two-phase "unwind then format" pipeline:
 *   Phase 1 (normalizeContent): Strip all formatting, sanitize encoding, merge paragraphs
 *   Phase 2 (per-file parseBlocks): Detect structure from clean text
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PDFPageLike = any;

// ============================================================================
// Character Replacement for WinAnsi Encoding
// ============================================================================

export const charReplacements: Record<string, string> = {
  // Arrows & dashes
  '\u2192': '->', // →
  '\u2190': '<-', // ←
  '\u2194': '<->', // ↔
  '\u2013': '-',  // – en dash
  '\u2014': '-',  // — em dash
  '\u2010': '-',  // ‐ hyphen
  '\u2011': '-',  // ‑ non-breaking hyphen
  '\u2012': '-',  // ‒ figure dash
  '\u2015': '-',  // ― horizontal bar

  // Quotation marks
  '\u2018': "'",  // ' left single
  '\u2019': "'",  // ' right single
  '\u201A': ',',  // ‚ single low-9
  '\u201C': '"',  // " left double
  '\u201D': '"',  // " right double
  '\u201E': '"',  // „ double low-9
  '\u2032': "'",  // ′ prime
  '\u2033': '"',  // ″ double prime
  '\u00AB': '"',  // « left guillemet
  '\u00BB': '"',  // » right guillemet
  '\u2039': "'",  // ‹ single left guillemet
  '\u203A': "'",  // › single right guillemet

  // Legal & common symbols
  '\u00A9': '(c)',     // ©
  '\u00AE': '(R)',     // ®
  '\u2122': '(TM)',    // ™
  '\u00A7': 'Section', // §
  '\u2020': '+',       // † dagger
  '\u2021': '++',      // ‡ double dagger

  // Bullets & shapes
  '\u2022': '*',  // •
  '\u25CF': '*',  // ●
  '\u25CB': 'o',  // ○
  '\u25A0': '[#]', // ■
  '\u25A1': '[ ]', // □
  '\u25B6': '>',  // ▶
  '\u25C0': '<',  // ◀
  '\u2605': '*',  // ★
  '\u2606': '*',  // ☆

  // Check marks & crosses
  '\u2713': '[check]', // ✓
  '\u2714': '[check]', // ✔
  '\u2715': '[x]', // ✕
  '\u2716': '[x]', // ✖
  '\u2717': '[x]', // ✗
  '\u2718': '[x]', // ✘

  // Math & comparison
  '\u00B0': 'deg', // °
  '\u00B1': '+/-', // ±
  '\u00D7': 'x',   // ×
  '\u00F7': '/',   // ÷
  '\u2264': '<=',  // ≤
  '\u2265': '>=',  // ≥
  '\u2260': '!=',  // ≠
  '\u2248': '~',   // ≈
  '\u221E': 'infinity', // ∞

  // Ellipsis & whitespace
  '\u2026': '...',  // …
  '\u00A0': ' ',    // non-breaking space
  '\u00AD': '-',    // soft hyphen
  '\u200B': '',     // zero-width space
  '\u200C': '',     // zero-width non-joiner
  '\u200D': '',     // zero-width joiner
  '\u2028': '\n',   // line separator
  '\u2029': '\n\n', // paragraph separator
  '\uFEFF': '',     // BOM / zero-width no-break space

  // Currency (outside WinAnsi range)
  '\u20AC': 'EUR',  // €

  // Extended math (from reportPdf.ts)
  '\u221A': 'sqrt',      // √
  '\u2211': 'sum',       // ∑
  '\u220F': 'prod',      // ∏
  '\u222B': 'integral',  // ∫
  '\u2202': 'd',         // ∂
  '\u2206': 'delta',     // ∆
  '\u2207': 'nabla',     // ∇
  '\u2208': 'in',        // ∈
  '\u2209': 'not in',    // ∉
  '\u220B': 'contains',  // ∋
  '\u2205': 'empty',     // ∅
  '\u2200': 'for all',   // ∀
  '\u2203': 'exists',    // ∃
  '\u2227': 'and',       // ∧
  '\u2228': 'or',        // ∨
  '\u00AC': 'not',       // ¬
  '\u2282': 'subset',    // ⊂
  '\u2283': 'superset',  // ⊃
  '\u2286': 'subseteq',  // ⊆
  '\u2287': 'superseteq', // ⊇
  '\u222A': 'union',     // ∪
  '\u2229': 'intersection', // ∩
  '\u2030': 'permille',  // ‰
};

// ============================================================================
// Character Encoding
// ============================================================================

/**
 * Strip all characters that cannot be encoded in WinAnsi.
 * Characters in the replacement table get substituted; others are dropped.
 */
export function sanitizeToWinAnsi(input: string): string {
  return Array.from(input || '')
    .map((ch) => {
      const code = ch.charCodeAt(0);
      if (code <= 0xff) return ch;
      if (charReplacements[ch]) return charReplacements[ch];
      return '';
    })
    .join('');
}

/**
 * Conditionally sanitize text for PDF rendering.
 * Unicode fonts: only replace problematic symbols, preserve CJK/Hangul etc.
 * Standard fonts: strip non-Latin characters via WinAnsi.
 */
export function sanitizeForPdf(input: string, useUnicodeFonts: boolean): string {
  if (useUnicodeFonts) {
    return Array.from(input || '')
      .map((ch) => {
        if (charReplacements[ch]) return charReplacements[ch];
        return ch;
      })
      .join('');
  }
  return sanitizeToWinAnsi(input);
}

// ============================================================================
// Content Cleaning
// ============================================================================

/**
 * Strip internal thinking/reasoning blocks from AI responses.
 */
export function stripThinkingBlocks(input: string): string {
  if (!input) return '';
  let cleaned = input;
  cleaned = cleaned.replace(/<think>[\s\S]*?<\/think>/gi, '');
  cleaned = cleaned.replace(/<\/?think>/gi, '');
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  return cleaned.trim();
}

/**
 * Strip ALL inline markdown formatting, producing plain text.
 * Structural markers (#, -, 1., |, Part X:) are preserved.
 */
export function stripAllMarkdownFormatting(input: string): string {
  let out = input || '';
  // Remove markdown links - keep label text, drop URL entirely
  out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  // Remove angle-bracket autolinks
  out = out.replace(/<(https?:\/\/[^>]+)>/g, '$1');
  // Remove HTML tags that might appear
  out = out.replace(/<\/?[a-z][a-z0-9]*[^>]*>/gi, '');
  // Remove triple-star bold-italic
  out = out.replace(/\*\*\*(.+?)\*\*\*/g, '$1');
  // Remove bold markers
  out = out.replace(/\*\*(.+?)\*\*/g, '$1');
  out = out.replace(/__(.+?)__/g, '$1');
  // Remove italic markers (be careful not to eat structural bullets/dashes)
  out = out.replace(/(?<!\w)\*([^*\n]+)\*(?!\w)/g, '$1');
  out = out.replace(/(?<!\w)_([^_\n]+)_(?!\w)/g, '$1');
  // Remove backticks (inline code)
  out = out.replace(/`+([^`]+)`+/g, '$1');
  // Remove strikethrough
  out = out.replace(/~~(.+?)~~/g, '$1');
  // Remove horizontal rules (standalone --- or *** or ___)
  out = out.replace(/^(---+|\*\*\*+|___+)$/gm, '');
  return out;
}

// ============================================================================
// Paragraph Merging
// ============================================================================

/**
 * Check if a trimmed line is a structural marker (heading, bullet, list, table, callout).
 * These lines should NOT be merged with adjacent lines.
 */
function isStructuralLine(trimmed: string): boolean {
  return (
    /^#{1,6}\s/.test(trimmed) ||                              // Headings
    /^[-*]\s+/.test(trimmed) ||                               // Bullets
    /^\d+\.\s+/.test(trimmed) ||                              // Numbered lists
    /^\[\d+\]\s+/.test(trimmed) ||                            // Bracket-numbered reference entries ([1] ..., [2] ...) — the deep-research mandated citation-list format (2026-08-16 citation-cutoff fix: these merged into one giant paragraph before)
    /^[IVX]{1,5}\.\s+/.test(trimmed) ||                       // Roman numeral sections (I. through XX.)
    /^\|/.test(trimmed) ||                                     // Table rows
    /^\*?\*?Part\s+\d+/i.test(trimmed) ||                     // Part headers
    /^\*?\*?(Important|Critical|Example|Note|Warning):/i.test(trimmed) || // Callouts
    /^\*\*(Included|NOT Included):?\*\*\s*$/i.test(trimmed)   // Bold labels
  );
}

/**
 * Merge adjacent non-blank, non-structural lines into single paragraphs.
 * This fixes AI content where sentences are broken across multiple source lines
 * (e.g., "An aggravated fel\nony" becomes "An aggravated felony").
 */
export function mergeContinuationLines(text: string): string {
  const lines = text.split('\n');
  const merged: string[] = [];
  let buffer = '';

  for (const raw of lines) {
    const trimmed = raw.trim();

    // Empty line = paragraph break
    if (!trimmed) {
      if (buffer) {
        merged.push(buffer);
        buffer = '';
      }
      merged.push('');
      continue;
    }

    // Structural lines always start a new block
    if (isStructuralLine(trimmed)) {
      if (buffer) {
        merged.push(buffer);
        buffer = '';
      }
      merged.push(trimmed);
      continue;
    }

    // Non-structural text line: merge with buffer
    if (buffer) {
      buffer = buffer + ' ' + trimmed;
    } else {
      buffer = trimmed;
    }
  }

  // Flush remaining buffer
  if (buffer) {
    merged.push(buffer);
  }

  return merged.join('\n');
}

// ============================================================================
// Content Normalization (Phase 1: "Unwind")
// ============================================================================

/**
 * Fully normalize raw AI markdown content before structural parsing.
 *
 * Pipeline:
 *  1. Strip thinking blocks
 *  2. Strip all inline markdown formatting
 *  3. Sanitize characters for target encoding
 *  4. Rejoin hyphenated words across line breaks
 *  5. Merge continuation lines into proper paragraphs
 *  6. Normalize whitespace
 *
 * Returns clean text with only structural markers preserved.
 */
export function normalizeContent(rawMarkdown: string, useUnicodeFonts: boolean): string {
  // Step 1: Strip thinking blocks
  let text = stripThinkingBlocks(rawMarkdown);

  // Step 2: Strip ALL inline markdown formatting
  text = stripAllMarkdownFormatting(text);

  // Step 3: Sanitize all characters for target encoding
  text = sanitizeForPdf(text, useUnicodeFonts);

  // Step 4: Rejoin hyphenated words across line breaks
  // Pattern: word ending in hyphen at end of line, next line starts with lowercase continuation
  text = text.replace(/(\w)-\n\s*([a-z])/g, '$1$2');

  // Step 5: Merge continuation lines into proper paragraphs
  text = mergeContinuationLines(text);

  // Step 6: Normalize whitespace
  text = text.replace(/[ \t]+/g, ' ');
  text = text.replace(/\n{3,}/g, '\n\n');

  return text.trim();
}

// ============================================================================
// Text Wrapping (Improved)
// ============================================================================

/**
 * Break a single word into fragments that each fit within maxWidth.
 * Used as a fallback when a word is wider than the available space.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function breakWord(word: string, font: any, size: number, maxWidth: number): string[] {
  const fragments: string[] = [];
  let fragment = '';

  for (const char of word) {
    const test = fragment + char;
    try {
      if (font.widthOfTextAtSize(test, size) > maxWidth) {
        if (fragment) fragments.push(fragment);
        fragment = char;
      } else {
        fragment = test;
      }
    } catch {
      // Character not encodable by font - skip it
      continue;
    }
  }

  if (fragment) fragments.push(fragment);
  return fragments.length ? fragments : [''];
}

/**
 * Wrap text to fit within maxWidth, splitting on word boundaries.
 * Falls back to character-level breaking for words wider than maxWidth.
 * Handles font encoding errors gracefully.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function wrapText(text: string, font: any, size: number, maxWidth: number): string[] {
  if (!text || !text.trim()) return [''];

  const words = text.split(/\s+/).filter(w => w.length > 0);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const testLine = current ? `${current} ${word}` : word;
    let width: number;
    try {
      width = font.widthOfTextAtSize(testLine, size);
    } catch {
      // Width calculation failed (unencodable chars) - flush current and skip
      if (current) lines.push(current);
      current = '';
      continue;
    }

    if (width <= maxWidth) {
      current = testLine;
    } else {
      if (current) lines.push(current);

      // Check if this single word exceeds maxWidth - need character-level breaking
      try {
        const wordWidth = font.widthOfTextAtSize(word, size);
        if (wordWidth > maxWidth) {
          const broken = breakWord(word, font, size, maxWidth);
          for (let i = 0; i < broken.length - 1; i++) {
            lines.push(broken[i]);
          }
          current = broken[broken.length - 1] || '';
        } else {
          current = word;
        }
      } catch {
        // Word itself has encoding issues - skip it
        current = '';
      }
    }
  }

  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

// ============================================================================
// Safe Text Drawing
// ============================================================================

/**
 * Aggressively sanitize text by testing each character against the font.
 * Only keeps characters the font can actually encode. Slow, but only
 * called on the error path.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function aggressiveSanitize(text: string, font: any, size: number): string {
  return Array.from(text)
    .filter((ch) => {
      try {
        font.widthOfTextAtSize(ch, size);
        return true;
      } catch {
        return false;
      }
    })
    .join('');
}

interface SafeDrawTextOptions {
  x: number;
  y: number;
  size: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  font: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  color?: any;
}

/**
 * Draw text on a PDF page with error recovery.
 *
 * Normal path: calls page.drawText() directly (zero overhead).
 * Error path: strips unencodable characters per-char, retries.
 * Last resort: renders nothing rather than crashing the PDF.
 */
export function safeDrawText(
  page: PDFPageLike,
  text: string,
  options: SafeDrawTextOptions,
): void {
  if (!text) return;
  try {
    page.drawText(text, options);
  } catch {
    // Attempt aggressive per-character re-sanitization
    const cleaned = aggressiveSanitize(text, options.font, options.size);
    if (cleaned) {
      try {
        page.drawText(cleaned, options);
      } catch {
        // Truly give up - but don't crash the PDF generation
        console.warn('[pdfTextUtils] Failed to render text even after sanitization:', text.slice(0, 50));
      }
    }
  }
}

// ============================================================================
// References Section Preservation
// ============================================================================

/**
 * Patterns to detect a references/bibliography section header in content.
 */
const REFS_HEADER_PATTERNS = [
  /\n(#{1,3}\s*(References|Bibliography|Source\s*Citations|Sources?\s*Cited|Works\s*Cited|Complete\s*Source\s*Citations))/im,
  /\n(\*{1,2}(References|Bibliography|Source\s*Citations|Sources?\s*Cited|Works\s*Cited)\*{1,2})/im,
  /\n((References|Bibliography|Source\s*Citations|Sources?\s*Cited|Works\s*Cited)\s*\n)/im,
  // 2026-08-16 citation-cutoff fix: the deep-research prompt's own section
  // header forms were unmatched, so the references body fell through
  // normalizeContent (URL stripping + line merging) as ordinary prose:
  //   "XV. Complete Source Citations" (roman-numeral mandated final section)
  //   "References and authorities cited" (plain-text legal header)
  /\n(#{0,3}\s*[IVX]{1,5}\.\s*(Complete\s*Source\s*Citations|References|Bibliography))/im,
  /\n(#{0,3}\s*\*{0,2}References\s+and\s+authorities\s+cited\*{0,2})/im,
];

/**
 * Split content into body and references section.
 * The references section is handled separately to preserve URLs
 * that would otherwise be stripped by normalizeContent.
 */
export function extractAndPreserveReferences(
  rawContent: string
): { body: string; references: string | null } {
  for (const pattern of REFS_HEADER_PATTERNS) {
    const match = rawContent.match(pattern);
    if (match && match.index !== undefined) {
      const splitIndex = match.index;
      const body = rawContent.slice(0, splitIndex);
      const references = rawContent.slice(splitIndex);
      return { body, references };
    }
  }

  return { body: rawContent, references: null };
}

/**
 * Convert markdown links in references to plaintext with visible URLs.
 * [Full Citation Text](https://example.com) -> Full Citation Text (https://example.com)
 * This preserves URL information for non-clickable formats (PDF, DOCX, print).
 */
export function convertReferencesToPlaintext(references: string): string {
  return references.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '$1 ($2)'
  );
}
